import { useEffect, useState, type ChangeEvent } from 'react'
import { api, type AccountMember, type InvitableAccount, type PendingInvite } from '../lib/api.ts'
import { Select } from '../components/Select.tsx'
import { Button } from '../components/Button.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { icons } from '../components/icons.ts'

/**
 * Usuários: who is a member here, and who has been invited (ADR 0024).
 *
 * A list like any other; opening a member is where their função and Formula
 * Certa user are chosen. Invites are a second list rather than rows mixed
 * into the first: an invite is a pending offer and a member is an account,
 * and blurring them would let somebody think withdrawing one removed the
 * other.
 *
 * A pharmacy's manager sees their pharmacy's people. A super admin sees
 * everybody in every pharmacy, with a filter by pharmacy, so nobody invited
 * into the wrong account goes missing.
 */
export function Users({
  onBack,
  onOpen,
  onInvite,
}: {
  onBack: () => void
  onOpen: (id: string) => void
  onInvite: () => void
}) {
  const [users, setUsers] = useState<AccountMember[]>([])
  const [invites, setInvites] = useState<PendingInvite[]>([])
  const [canInvite, setCanInvite] = useState(false)
  // Every pharmacy, for a super admin; a pharmacy's manager sees theirs only.
  const [accounts, setAccounts] = useState<InvitableAccount[]>([])
  const [pharmacy, setPharmacy] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(true)

  function load() {
    setLoading(true)
    setError(undefined)
    void api
      .users()
      .then((r) => {
        setUsers(r.users)
        setAccounts(r.accounts)
        setInvites(r.invites)
        setCanInvite(r.canInvite)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Usuários</h1>
        {canInvite && (
          <Button
            className="ml-auto"
            variant="primary"
            tone="brand"
            size="small"
            leadingIcon={icons.invite}
            onClick={onInvite}
          >
            Convidar
          </Button>
        )}
      </div>

      {accounts.length > 0 && (
        <div className="w-64">
          <Select
            label="Farmácia"
            value={pharmacy}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setPharmacy(e.target.value)}
          >
            <option value="">Todas</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </Select>
        </div>
      )}

      <DataTable
        columns={MEMBER_COLUMNS}
        rows={
          pharmacy === ''
            ? users
            : users.filter((u) => u.memberships.some((m) => m.account_id === pharmacy))
        }
        rowKey={(u) => u.user_id}
        loading={loading}
        error={error}
        onRetry={load}
        onRowClick={(u) => onOpen(u.user_id)}
        pageSize={6}
        minWidth="44rem"
        empty="Ninguém ainda."
      />

      {invites.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-tPSemi text-text-strong">Convites pendentes ({invites.length})</h2>
          <DataTable
            columns={INVITE_COLUMNS}
            rows={invites}
            rowKey={(i) => i.id}
            minWidth="36rem"
          />
        </section>
      )}
    </div>
  )
}

const MEMBER_COLUMNS: ReadonlyArray<Column<AccountMember>> = [
  {
    key: 'who',
    header: 'Usuário',
    render: (u) => (
      <span className="flex flex-col">
        <span className="text-text-strong">{u.name ?? u.email}</span>
        {u.name !== null && <span className="text-tSmall text-text-weak">{u.email}</span>}
      </span>
    ),
  },
  {
    key: 'pharmacies',
    header: 'Farmácias',
    render: (u) => (
      <span className="flex flex-col gap-0.5">
        {/* A super admin sees everything by being ours, whatever the role says. */}
        {u.system_role === 'super_admin' && (
          <span className="text-tSmall text-text-weak">super admin · vê tudo</span>
        )}
        {u.memberships.map((m) => (
          <span key={m.account_id} className="text-tSmall">
            <span className="text-text-strong">{m.account_name}</span>
            <span className="text-text-weak"> · </span>
            {m.role_name === null ? (
              // Named, because no role is a person who sees nothing.
              <span className="text-text-error">sem função</span>
            ) : (
              <span className="text-text-strong">{m.role_name}</span>
            )}
            {m.erp_user !== null && (
              <span className="text-text-weak font-mono"> · {m.erp_user}</span>
            )}
          </span>
        ))}
      </span>
    ),
  },
  {
    key: 'active',
    header: 'Situação',
    className: 'text-text-weak',
    render: (u) => (u.active ? 'Ativo' : 'Inativo'),
  },
]

const INVITE_COLUMNS: ReadonlyArray<Column<PendingInvite>> = [
  { key: 'email', header: 'E-mail', render: (i) => i.email },
  {
    key: 'pharmacy',
    header: 'Farmácia',
    className: 'text-text-weak',
    render: (i) => i.account_name,
  },
  {
    key: 'role',
    header: 'Função',
    className: 'text-text-weak',
    render: (i) => (i.role === 'super_admin' ? 'super admin' : (i.role_name ?? '—')),
  },
  {
    key: 'expires',
    header: 'Expira em',
    className: 'text-tSmall text-text-weak',
    render: (i) => new Date(i.expires_at).toLocaleDateString('pt-BR'),
  },
]
